import { useMutation, useQuery } from '@tanstack/react-query';
import { type MouseEvent as ReactMouseEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Document, Page } from 'react-pdf';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { formatApiMessage } from '@/api/client';
import { type ReviewComment, type SignContext, type SignField, signApi } from '@/api/sign.api';
import { SignerLayout } from '@/components/layout/SignerLayout';
import { SignaturePad } from '@/components/sign/SignaturePad';
import { FontSignaturePad } from '@/components/sign/FontSignaturePad';
import { cn } from '@/lib/cn';
import '@/lib/pdfWorker';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

type Stage = 'loading' | 'consent' | 'awaiting-otp' | 'verifying' | 'signing' | 'completed' | 'declined' | 'error';

export function SignPage() {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const [stage, setStage] = useState<Stage>('loading');
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  // False while the session is the link-only 'review' one: the document is
  // readable and notes can be left, but signing/declining needs the email code.
  const [verified, setVerified] = useState(false);
  const [otp, setOtp] = useState('');
  const [consent, setConsent] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const { data: ctx, isError, error } = useQuery({
    queryKey: ['sign', token],
    queryFn: () => signApi.load(token!),
    enabled: !!token,
    retry: false,
    refetchOnWindowFocus: false,
  });

  // Initial transition: once context loads, show consent.
  useEffect(() => {
    if (ctx) {
      if (ctx.recipient.status === 'signed') setStage('completed');
      else if (ctx.recipient.status === 'declined') setStage('declined');
      else setStage((prev) => (prev === 'loading' ? 'consent' : prev));
    }
  }, [ctx]);

  useEffect(() => {
    if (isError) {
      setStage('error');
      setErrorMsg(formatApiMessage(error));
    }
  }, [isError, error]);

  // Consent → straight into the document with a review-only session. The code
  // is asked for later, when the recipient moves on to signing.
  const startReview = useMutation({
    mutationFn: () => signApi.startReviewSession(token!),
    onSuccess: (data) => {
      setSessionToken(data.sessionToken);
      setVerified(false);
      setStage('signing');
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  const requestOtp = useMutation({
    mutationFn: () => signApi.requestOtp(token!),
    onSuccess: () => {
      setOtp('');
      setStage('awaiting-otp');
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  const verifyOtp = useMutation({
    mutationFn: () => signApi.verifyOtp(token!, otp),
    onSuccess: (data) => {
      setSessionToken(data.sessionToken);
      setVerified(true);
      setStage('signing');
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  if (!token) {
    return (
      <SignerLayout>
        <ErrorCard message="No token provided" />
      </SignerLayout>
    );
  }
  if (stage === 'loading' || !ctx) {
    return (
      <SignerLayout>
        <div className="max-w-md mx-auto card text-center py-12">
          <div className="text-muted">Loading…</div>
        </div>
      </SignerLayout>
    );
  }
  if (stage === 'error') {
    return (
      <SignerLayout>
        <ErrorCard message={errorMsg ?? t('sign_page.invalid_link_default')} />
      </SignerLayout>
    );
  }
  if (stage === 'completed') {
    return (
      <SignerLayout organizationName={ctx.sender.organizationName}>
        <SuccessCard ctx={ctx} />
      </SignerLayout>
    );
  }
  if (stage === 'declined') {
    return (
      <SignerLayout organizationName={ctx.sender.organizationName}>
        <DeclinedCard />
      </SignerLayout>
    );
  }

  if (stage === 'consent') {
    return (
      <SignerLayout organizationName={ctx.sender.organizationName}>
        <ConsentCard
          ctx={ctx}
          consent={consent}
          onConsent={setConsent}
          onProceed={() => startReview.mutate()}
          loading={startReview.isPending}
        />
      </SignerLayout>
    );
  }

  if (stage === 'awaiting-otp') {
    return (
      <SignerLayout organizationName={ctx.sender.organizationName}>
        <OtpCard
          ctx={ctx}
          otp={otp}
          onOtpChange={setOtp}
          onVerify={() => verifyOtp.mutate()}
          onResend={() => requestOtp.mutate()}
          loading={verifyOtp.isPending}
          onBack={sessionToken ? () => setStage('signing') : undefined}
        />
      </SignerLayout>
    );
  }

  // stage === 'signing'
  // for_info recipients see a much simpler "I've reviewed this" card instead
  // of the full signing UI — they have no fields to fill, just need to record
  // acknowledgement so the sender knows the doc was seen.
  if (ctx.recipient.role === 'for_info') {
    return (
      <SignerLayout organizationName={ctx.sender.organizationName}>
        <AcknowledgeStage
          ctx={ctx}
          token={token}
          sessionToken={sessionToken!}
          verified={verified}
          onNeedVerification={() => requestOtp.mutate()}
          verifying={requestOtp.isPending}
          onCompleted={() => setStage('completed')}
        />
      </SignerLayout>
    );
  }
  return (
    <SignerLayout organizationName={ctx.sender.organizationName}>
      <SigningStage
        ctx={ctx}
        token={token}
        sessionToken={sessionToken!}
        verified={verified}
        onNeedVerification={() => requestOtp.mutate()}
        verifying={requestOtp.isPending}
        onCompleted={() => setStage('completed')}
        onDeclined={() => setStage('declined')}
      />
    </SignerLayout>
  );
}

// ────────────────────────────────────────────────────────────────

function ConsentCard({
  ctx,
  consent,
  onConsent,
  onProceed,
  loading,
}: {
  ctx: SignContext;
  consent: boolean;
  onConsent: (v: boolean) => void;
  onProceed: () => void;
  loading: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="max-w-xl mx-auto">
      <div className="card">
        <div className="text-[12px] uppercase font-semibold text-muted tracking-wide">{t('common.from')}</div>
        <div className="text-[15px] font-medium text-gray-900 mt-0.5">
          {ctx.sender.firstName} {ctx.sender.lastName} · {ctx.sender.organizationName}
        </div>
        <div className="text-[12px] text-muted mt-0.5">{ctx.sender.email}</div>

        <hr className="my-4 border-border" />

        <div className="text-[12px] uppercase font-semibold text-muted tracking-wide">{t('detail.documents')}</div>
        <div className="text-[18px] font-semibold text-gray-900 mt-0.5">{ctx.envelope.title}</div>
        {ctx.envelope.message && (
          <p className="mt-3 text-[13px] text-gray-700 bg-bg p-3 rounded border border-border">
            {ctx.envelope.message}
          </p>
        )}

        <SignChainView ctx={ctx} />

        <hr className="my-4 border-border" />

        <p className="text-[13px] text-gray-700 leading-relaxed">
          {t('sign_page.greet', { name: ctx.recipient.name, role: t(`recipients_step.role_${ctx.recipient.role}`) })}
        </p>

        <label className="flex items-start gap-2 mt-4 text-[13px] text-gray-700 cursor-pointer">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => onConsent(e.target.checked)}
            className="mt-0.5 accent-primary"
          />
          <span>{t('sign_page.consent_text')}</span>
        </label>

        <button
          onClick={onProceed}
          className="btn btn-primary w-full mt-5"
          disabled={!consent || loading}
        >
          {loading
            ? t('common.loading')
            : t('sign_page.open_document', 'Review the document')}
        </button>
        <p className="text-[12px] text-muted text-center mt-2">
          {t(
            'sign_page.code_later_hint',
            'You can read the document and add notes first — the email code is only needed to sign.',
          )}
        </p>
      </div>
    </div>
  );
}

function OtpCard({
  ctx,
  otp,
  onOtpChange,
  onVerify,
  onResend,
  loading,
  onBack,
}: {
  ctx: SignContext;
  otp: string;
  onOtpChange: (v: string) => void;
  onVerify: () => void;
  onResend: () => void;
  loading: boolean;
  /** Present once a review session exists — lets the recipient step back to
   *  the document instead of being trapped on the code screen. */
  onBack?: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="max-w-md mx-auto">
      <div className="card text-center">
        <div className="text-3xl mb-2">📧</div>
        <h2 className="text-[18px] font-semibold text-gray-900">{t('sign_page.check_email_title')}</h2>
        <p className="text-[13px] text-muted mt-1">
          {t('sign_page.code_sent_to')} <strong className="text-gray-900">{ctx.recipient.email}</strong>.
        </p>

        <input
          autoFocus
          maxLength={6}
          inputMode="numeric"
          pattern="[0-9]*"
          value={otp}
          onChange={(e) => onOtpChange(e.target.value.replace(/\D/g, ''))}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && otp.length === 6) onVerify();
          }}
          className="w-full text-center text-[28px] font-bold tracking-[0.4em] py-3 rounded-md border border-border-strong outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 mt-5"
          placeholder="000000"
        />

        <button
          onClick={onVerify}
          className="btn btn-primary w-full mt-4"
          disabled={otp.length !== 6 || loading}
        >
          {loading ? t('sign_page.verifying') : t('sign_page.verify_btn')}
        </button>

        <button
          onClick={onResend}
          className="text-[12px] text-muted hover:text-gray-900 mt-3"
        >
          {t('sign_page.didnt_get_resend')}
        </button>

        {onBack && (
          <div>
            <button
              onClick={onBack}
              className="text-[12px] text-muted hover:text-gray-900 mt-2"
            >
              ← {t('sign_page.back_to_document', 'Back to the document')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Stage rendered when the recipient's role is `for_info`: no fields to fill,
 * no signature canvas — they just preview the PDF and click "I've reviewed".
 *
 * Backend treats this exactly like sign() with no fieldValues — the audit
 * log records `envelope.acknowledged` instead of `envelope.signed` so the
 * sender can tell who acted vs. who merely received an FYI.
 */
function AcknowledgeStage({
  ctx,
  token,
  sessionToken,
  verified,
  onNeedVerification,
  verifying,
  onCompleted,
}: {
  ctx: SignContext;
  token: string;
  sessionToken: string;
  verified: boolean;
  onNeedVerification: () => void;
  verifying: boolean;
  onCompleted: () => void;
}) {
  const { t } = useTranslation();
  const [activeDocId, setActiveDocId] = useState<string>(ctx.documents[0]?.id ?? '');
  const [page, setPage] = useState(1);
  const [pdfPages, setPdfPages] = useState(
    ctx.documents.find((d) => d.id === activeDocId)?.pagesCount ?? 1,
  );

  const acknowledge = useMutation({
    mutationFn: () => signApi.complete(token, sessionToken, []),
    onSuccess: () => onCompleted(),
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  const docFile = useMemo(
    () => ({
      url: signApi.documentUrl(token, activeDocId),
      httpHeaders: { 'x-sign-session': sessionToken },
    }),
    [token, activeDocId, sessionToken],
  );

  return (
    <div className="max-w-5xl mx-auto">
      <div className="card mb-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase font-semibold text-primary tracking-wider mb-1">
            {t('sign_page.fyi_badge')}
          </div>
          <h2 className="text-[18px] font-semibold text-gray-900 m-0">{ctx.envelope.title}</h2>
          <p className="text-[12px] text-muted mt-1">
            {t('sign_page.fyi_explain', {
              sender: `${ctx.sender.firstName} ${ctx.sender.lastName}`.trim() || ctx.sender.email,
            })}
          </p>
        </div>
        <button
          onClick={() => (verified ? acknowledge.mutate() : onNeedVerification())}
          disabled={acknowledge.isPending || verifying}
          className="btn btn-primary"
        >
          {acknowledge.isPending || verifying
            ? t('common.sending')
            : `✓ ${t('sign_page.acknowledge_btn')}`}
        </button>
      </div>

      {ctx.documents.length > 1 && (
        <div className="card !p-2 mb-3 flex gap-2 items-center flex-wrap">
          {ctx.documents.map((d) => (
            <button
              key={d.id}
              onClick={() => {
                setActiveDocId(d.id);
                setPage(1);
                setPdfPages(d.pagesCount);
              }}
              className={`text-[12px] px-2.5 py-1 rounded-md ${
                activeDocId === d.id ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700'
              }`}
            >
              {d.originalName}
            </button>
          ))}
        </div>
      )}

      <div className="card !p-0 overflow-hidden">
        {pdfPages > 1 && (
          <div className="px-3 py-1.5 border-b border-border flex items-center justify-between text-[12px] text-muted">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="btn btn-ghost btn-sm"
            >
              ←
            </button>
            <span>{t('common.page')} {page} / {pdfPages}</span>
            <button
              onClick={() => setPage((p) => Math.min(pdfPages, p + 1))}
              disabled={page >= pdfPages}
              className="btn btn-ghost btn-sm"
            >
              →
            </button>
          </div>
        )}
        <Document
          file={docFile}
          onLoadSuccess={(p) => setPdfPages(p.numPages)}
          loading={<div className="p-12 text-center text-muted">{t('fields_step.loading_pdf')}</div>}
          error={<div className="p-12 text-center text-danger">{t('fields_step.failed_to_load_pdf')}</div>}
        >
          <Page
            pageNumber={page}
            width={820}
            renderAnnotationLayer={false}
            renderTextLayer={false}
          />
        </Document>
      </div>
    </div>
  );
}

function SigningStage({
  ctx,
  token,
  sessionToken,
  verified,
  onNeedVerification,
  verifying,
  onCompleted,
  onDeclined,
}: {
  ctx: SignContext;
  token: string;
  sessionToken: string;
  /** True once the email code was verified. While false the session is
   *  review-only: the PDF is readable and notes can be added, but every final
   *  action (sign / decline / request revision) must go through the code. */
  verified: boolean;
  onNeedVerification: () => void;
  verifying: boolean;
  onCompleted: () => void;
  onDeclined: () => void;
}) {
  const { t } = useTranslation();
  const [activeDocId, setActiveDocId] = useState<string>(ctx.documents[0]?.id ?? '');
  const [page, setPage] = useState(1);
  const [values, setValues] = useState<Record<string, string>>({});
  // Optional free-text note the recipient adds when signing/approving.
  const [note, setNote] = useState('');
  const [activeFieldId, setActiveFieldId] = useState<string | null>(null);
  const [showDecline, setShowDecline] = useState(false);
  const [showRevisionReq, setShowRevisionReq] = useState(false);
  const [pdfPages, setPdfPages] = useState(ctx.documents.find((d) => d.id === activeDocId)?.pagesCount ?? 1);
  const stageRef = useRef<HTMLDivElement | null>(null);

  // ─── Notes placed directly on the PDF (approver self-placed) ───
  // Positioned notes live in envelope.metadata.reviewComments (shared with the
  // page-level notes panel). We lift the list to this level so the on-page pins
  // and the side panel stay in sync, and so a freshly placed note appears at
  // once without a context refetch.
  const [comments, setComments] = useState<ReviewComment[]>(ctx.reviewComments ?? []);
  // When note-placement mode is on, clicking the PDF drops a pin the approver
  // then types into. `draftPin` holds the pending pin until it's saved/cancelled.
  const [noteMode, setNoteMode] = useState(false);
  const [draftPin, setDraftPin] = useState<{ xPct: number; yPct: number; text: string } | null>(null);
  const pdfWrapRef = useRef<HTMLDivElement | null>(null);

  const placeNoteMutation = useMutation({
    mutationFn: (input: { xPct: number; yPct: number; text: string }) =>
      signApi.addComment(token, sessionToken, {
        page,
        text: input.text.trim(),
        xPct: input.xPct,
        yPct: input.yPct,
        documentId: activeDocId,
      }),
    onSuccess: (res) => {
      setComments(res.comments);
      setDraftPin(null);
      toast.success(t('sign_page.comment_added', 'Note added'));
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  // Drop a draft pin where the approver clicked (only while note mode is on).
  function handlePdfClick(e: ReactMouseEvent<HTMLDivElement>) {
    if (!noteMode) return;
    const rect = pdfWrapRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0 || rect.height === 0) return;
    const xPct = ((e.clientX - rect.left) / rect.width) * 100;
    const yPct = ((e.clientY - rect.top) / rect.height) * 100;
    setDraftPin({
      xPct: Math.min(100, Math.max(0, xPct)),
      yPct: Math.min(100, Math.max(0, yPct)),
      text: '',
    });
  }

  const commentsHere = comments.filter(
    (c) => c.page === page && typeof c.xPct === 'number' && typeof c.yPct === 'number',
  );

  // Pre-fill name/email/date fields from context.
  useEffect(() => {
    const seed: Record<string, string> = {};
    for (const f of ctx.fields) {
      if (f.type === 'name') seed[f.id] = ctx.recipient.name;
      else if (f.type === 'email') seed[f.id] = ctx.recipient.email;
      else if (f.type === 'date') seed[f.id] = new Date().toLocaleDateString('az-AZ');
      // Pre-fill note fields with the creator's authored text so the approver
      // sees it and can keep or edit it.
      else if (f.type === 'note' && f.defaultValue) seed[f.id] = f.defaultValue;
    }
    setValues((prev) => ({ ...seed, ...prev }));
  }, [ctx]);

  const fieldsHere = ctx.fields.filter(
    (f) => f.documentId === activeDocId && f.pageNumber === page,
  );

  // When the envelope allows unsigned acceptance, signature/initial/image
  // fields don't gate completion — the signer can confirm without signing.
  const sigOptional = ctx.envelope.signatureOptional;
  const isSignatureField = (f: SignField) =>
    f.type === 'signature' || f.type === 'initial' || f.type === 'image';
  const requiredFields = ctx.fields.filter(
    (f) => f.isRequired && !(sigOptional && isSignatureField(f)),
  );
  const totalRequired = requiredFields.length;
  const filledRequired = requiredFields.filter((f) => (values[f.id] ?? '').trim() !== '').length;
  const allFilled = filledRequired === totalRequired;
  // On a signatureOptional envelope, if the signer left every signature field
  // empty, the finish action is really an "accept without signing".
  const anySignatureFilled = ctx.fields.some(
    (f) => isSignatureField(f) && (values[f.id] ?? '').trim() !== '',
  );

  const completeMutation = useMutation({
    mutationFn: () =>
      signApi.complete(
        token,
        sessionToken,
        Object.entries(values).map(([id, value]) => ({ id, value })),
        note.trim() || undefined,
      ),
    onSuccess: () => onCompleted(),
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  const declineMutation = useMutation({
    mutationFn: (reason: string) => signApi.decline(token, sessionToken, reason),
    onSuccess: () => onDeclined(),
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  const requestRevisionMutation = useMutation({
    mutationFn: (reason: string) => signApi.requestRevision(token, sessionToken, reason),
    onSuccess: () => {
      toast.success(t('sign_page.revision_sent_toast'));
      onDeclined();
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  const docFile = useMemo(
    () => ({
      url: signApi.documentUrl(token, activeDocId),
      httpHeaders: { 'x-sign-session': sessionToken },
    }),
    [token, activeDocId, sessionToken],
  );

  function setValue(id: string, value: string) {
    setValues((v) => ({ ...v, [id]: value }));
  }

  const isApprover = ctx.recipient.role === 'approver';

  // Everyone lands on an explicit review step first — read the document and
  // drop notes into the PDF — and only then move on to the email code and the
  // final action. Once the code is verified this component remounts with
  // `verified`, so it opens straight on the sign/approve screen.
  const [phase, setPhase] = useState<'review' | 'sign'>(verified ? 'sign' : 'review');
  const reviewing = phase === 'review';

  // Final actions need an OTP-verified session; from the review step they send
  // the recipient to the code screen instead of failing at the API.
  function guarded(action: () => void) {
    return () => (verified ? action() : onNeedVerification());
  }

  return (
    <div ref={stageRef}>
      {/* Top bar */}
      <div className="card !p-3 mb-4 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase font-semibold text-muted tracking-wide">
            {isApprover
              ? t('sign_page.approval')
              : reviewing
                ? t('sign_page.review_title', 'Review document')
                : t('sign_page.signing_title')}
          </div>
          <div className="font-medium text-gray-900">{ctx.envelope.title}</div>
        </div>
        <div className="flex items-center gap-3">
          {!isApprover && !reviewing && (
            <>
              <div className="text-[12px] text-muted">
                {t('sign_page.filled_status')} <strong className="text-gray-900">{filledRequired}/{totalRequired}</strong>
              </div>
              <div className="progress w-32">
                <div className="progress-bar" style={{ width: `${totalRequired ? (filledRequired/totalRequired)*100 : 0}%` }} />
              </div>
            </>
          )}
          {ctx.envelope.previousRevision && (
            <button
              onClick={async () => {
                try {
                  const url = await signApi.previousDocumentBlobUrl(token, sessionToken);
                  window.open(url, '_blank', 'noopener');
                } catch {
                  toast.error(t('sign_page.previous_version_error'));
                }
              }}
              className="btn btn-ghost"
              title={t('sign_page.view_previous_hint') as string}
            >
              🗄 {t('sign_page.view_previous_btn')}
            </button>
          )}
          <button
            onClick={guarded(() => setShowRevisionReq(true))}
            className="btn btn-ghost text-warning-fg"
            title={t('sign_page.revision_btn_hint') as string}
          >
            ✎ {t('sign_page.request_revision_btn')}
          </button>
          <button
            onClick={guarded(() => setShowDecline(true))}
            className="btn btn-ghost text-danger"
          >
            {isApprover ? t('admin_users.reject') : t('sign_page.decline_btn')}
          </button>
          {!isApprover && !reviewing && (
            <button
              onClick={() => { setPhase('review'); setPage(1); }}
              className="btn btn-ghost"
            >
              ← {t('sign_page.back_to_review', 'Back to review')}
            </button>
          )}
          {reviewing ? (
            <button
              onClick={() => {
                setNoteMode(false);
                setDraftPin(null);
                // Not verified yet → the code screen; the review session stays
                // alive so "back to the document" still works from there.
                if (!verified) return onNeedVerification();
                setPhase('sign');
              }}
              disabled={verifying}
              className="btn btn-primary"
            >
              {verifying
                ? t('sign_page.sending_code')
                : `${
                    isApprover
                      ? t('sign_page.continue_to_approve', 'Continue to approval')
                      : t('sign_page.continue_to_sign', 'Continue to signing')
                  } →`}
            </button>
          ) : (
            <button
              onClick={() => completeMutation.mutate()}
              disabled={(!isApprover && !allFilled) || completeMutation.isPending}
              className="btn btn-primary"
            >
              {completeMutation.isPending
                ? t('common.submitting')
                : isApprover
                ? t('sign_page.approve_btn')
                : sigOptional && !anySignatureFilled
                ? t('sign_page.accept_without_sign')
                : t('sign_page.finish_sign')}
            </button>
          )}
        </div>
      </div>

      {reviewing && (
        <div className="card !p-3 mb-4 bg-warning-bg border-warning text-[13px] text-warning-fg">
          👀 {t(
            'sign_page.review_banner',
            'Step 1 of 2 — review the document. You can add notes inside the PDF before you sign.',
          )}
          {!verified && (
            <span className="block mt-1 opacity-80">
              📧 {t(
                'sign_page.code_on_continue_hint',
                'The verification code is sent to your email only when you continue.',
              )}
            </span>
          )}
        </div>
      )}

      {sigOptional && !isApprover && !reviewing && (
        <div className="card !p-3 mb-4 bg-info-bg border-info text-[13px] text-info-fg">
          ℹ️ {t('sign_page.signature_optional_note')}
        </div>
      )}

      <div className="grid grid-cols-[1fr_320px] gap-4">
        {/* PDF + fields */}
        <main>
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            {ctx.documents.length > 1 && (
              <select
                className="input !py-1.5 max-w-xs text-[13px]"
                value={activeDocId}
                onChange={(e) => {
                  setActiveDocId(e.target.value);
                  setPage(1);
                  const doc = ctx.documents.find((d) => d.id === e.target.value);
                  if (doc) setPdfPages(doc.pagesCount);
                }}
              >
                {ctx.documents.map((d) => (
                  <option key={d.id} value={d.id}>{d.originalName}</option>
                ))}
              </select>
            )}
            <div className="flex items-center gap-2 ml-auto">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="btn btn-secondary btn-sm"
              >←</button>
              <span className="text-[13px] text-muted">{t('common.page')} {page} / {pdfPages}</span>
              <button
                onClick={() => setPage((p) => Math.min(pdfPages, p + 1))}
                disabled={page >= pdfPages}
                className="btn btn-secondary btn-sm"
              >→</button>
            </div>
          </div>

          {/* Review toolbar. Every recipient who acts on the document — signer
              or approver, external guest or IsoSofts account holder — reviews
              it here and may drop notes into the PDF before signing. */}
          <div className="flex items-center gap-2 mb-2">
            <button
              type="button"
              onClick={() => { setNoteMode((v) => !v); setDraftPin(null); }}
              className={cn(
                'btn btn-sm',
                noteMode ? 'btn-primary' : 'btn-secondary',
              )}
            >
              🗒️ {noteMode
                ? t('sign_page.place_note_active', 'Click on the document…')
                : t('sign_page.place_note', 'Add note on document')}
            </button>
            <span className="text-[11px] text-muted">
              {noteMode
                ? t('sign_page.place_note_hint', 'Click where the note should appear.')
                : isApprover
                  ? ''
                  : t(
                      'sign_page.review_before_sign_hint',
                      'Review the document before signing — you can add notes inside the PDF.',
                    )}
            </span>
          </div>

          <div
            ref={pdfWrapRef}
            onClick={handlePdfClick}
            className={cn(
              'relative bg-white border border-border rounded-md inline-block',
              noteMode && 'cursor-crosshair',
            )}
          >
            <Document
              file={docFile}
              onLoadSuccess={(p) => setPdfPages(p.numPages)}
              loading={<div className="p-12 text-muted">{t('fields_step.loading_pdf')}</div>}
              error={<div className="p-12 text-danger">{t('fields_step.failed_to_load_pdf')}</div>}
            >
              <Page
                pageNumber={page}
                width={620}
                renderAnnotationLayer={false}
                renderTextLayer={false}
              />
            </Document>

            {fieldsHere.map((f) => (
              <FieldOverlay
                key={f.id}
                field={f}
                value={values[f.id] ?? ''}
                active={activeFieldId === f.id}
                inert={noteMode || reviewing}
                muted={reviewing}
                onClick={() => setActiveFieldId(f.id)}
              />
            ))}

            {/* Already-placed notes on this page (read-only pins). */}
            {commentsHere.map((c) => (
              <NotePin key={c.id} xPct={c.xPct!} yPct={c.yPct!} author={c.author} text={c.text} />
            ))}

            {/* Pending note being typed. */}
            {draftPin && (
              <NoteDraftEditor
                xPct={draftPin.xPct}
                yPct={draftPin.yPct}
                value={draftPin.text}
                saving={placeNoteMutation.isPending}
                onChange={(text) => setDraftPin((d) => (d ? { ...d, text } : d))}
                onCancel={() => setDraftPin(null)}
                onSave={() => {
                  if (draftPin.text.trim()) placeNoteMutation.mutate(draftPin);
                }}
              />
            )}
          </div>
        </main>

        {/* Side: review guidance (review step), field input (signers) or the
            approval panel (approvers). */}
        <aside className="space-y-3">
          {reviewing ? (
            <div className="card !p-4 space-y-3">
              <div className="text-[11px] uppercase font-semibold text-muted tracking-wide">
                {t('sign_page.review_title', 'Review document')}
              </div>
              <p className="text-[13px] text-gray-700 leading-relaxed">
                {t(
                  'sign_page.review_instructions',
                  'Read the document page by page. To leave a remark, click "Add note on document" and then click the spot on the page — your note stays inside the signed PDF and the sender sees it.',
                )}
              </p>
              {ctx.fields.length > 0 && (
                <div className="text-[12px] text-muted border-t border-border pt-3">
                  {t(
                    'sign_page.review_fields_hint',
                    'The faded boxes show where you will sign or fill in. Continue to signing when you are ready.',
                  )}
                </div>
              )}
            </div>
          ) : isApprover && ctx.fields.length === 0 ? (
            <div className="card !p-4 space-y-3">
              <div className="text-[11px] uppercase font-semibold text-muted tracking-wide">
                {t('sign_page.your_role_approver')}
              </div>
              <p className="text-[13px] text-gray-700">
                {t('sign_page.approver_instructions')}
              </p>
              <div className="text-[12px] text-muted border-t border-border pt-3">
                {t('sign_page.approver_no_fields')}
              </div>
            </div>
          ) : (
            <FieldInputPanel
              ctx={ctx}
              activeFieldId={activeFieldId}
              values={values}
              onJump={(f) => {
                if (f.documentId !== activeDocId) {
                  setActiveDocId(f.documentId);
                  const doc = ctx.documents.find((d) => d.id === f.documentId);
                  if (doc) setPdfPages(doc.pagesCount);
                }
                setPage(f.pageNumber);
                setActiveFieldId(f.id);
              }}
              onChange={setValue}
            />
          )}

          {/* Optional free-text note surfaced to the creator in the activity
              log — part of the signing action, so it's hidden while reviewing. */}
          {!reviewing && (
          <div className="card !p-4">
            <label className="text-[11px] uppercase font-semibold text-muted tracking-wide block mb-1.5">
              {t('sign_page.note_label')}
            </label>
            <textarea
              className="input min-h-[70px] text-[13px]"
              placeholder={t('sign_page.note_placeholder') as string}
              value={note}
              maxLength={2000}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
          )}

          {/* Notes placed inside the document, anchored to the page being viewed. */}
          <ReviewCommentsPanel
            token={token}
            sessionToken={sessionToken}
            currentPage={page}
            comments={comments}
            onCommentsChange={setComments}
          />
        </aside>
      </div>

      {showDecline && (
        <DeclineModal
          onClose={() => setShowDecline(false)}
          onSubmit={(reason) => {
            declineMutation.mutate(reason);
            setShowDecline(false);
          }}
        />
      )}
      {showRevisionReq && (
        <RevisionRequestModal
          pending={requestRevisionMutation.isPending}
          onClose={() => setShowRevisionReq(false)}
          onSubmit={(reason) => {
            requestRevisionMutation.mutate(reason);
            setShowRevisionReq(false);
          }}
        />
      )}
    </div>
  );
}

// Reviewer/approver notes anchored to the page currently open. Each note is
// tagged with that page so the creator can see exactly where a remark applies.
function ReviewCommentsPanel({
  token,
  sessionToken,
  currentPage,
  comments,
  onCommentsChange,
}: {
  token: string;
  sessionToken: string;
  currentPage: number;
  comments: ReviewComment[];
  onCommentsChange: (comments: ReviewComment[]) => void;
}) {
  const { t } = useTranslation();
  const [text, setText] = useState('');

  const addMutation = useMutation({
    mutationFn: () =>
      signApi.addComment(token, sessionToken, { page: currentPage, text: text.trim() }),
    onSuccess: (res) => {
      onCommentsChange(res.comments);
      setText('');
      toast.success(t('sign_page.comment_added', 'Note added'));
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  return (
    <div className="card !p-4">
      <label className="text-[11px] uppercase font-semibold text-muted tracking-wide block mb-1.5">
        {t('sign_page.comments_label', 'Notes on the document')}
      </label>

      {comments.length > 0 && (
        <ul className="space-y-1.5 mb-2 max-h-40 overflow-y-auto">
          {comments.map((c) => (
            <li key={c.id} className="text-[12px] bg-gray-50 border border-border rounded-md px-2 py-1.5">
              <div className="flex items-center justify-between gap-2 text-[10px] text-muted mb-0.5">
                <span className="font-semibold text-gray-700">{c.author}</span>
                <span>
                  {typeof c.xPct === 'number' && '📍 '}
                  {t('sign_page.comment_page', 'Page')} {c.page}
                </span>
              </div>
              <div className="text-gray-900 whitespace-pre-wrap">{c.text}</div>
            </li>
          ))}
        </ul>
      )}

      <textarea
        className="input min-h-[56px] text-[13px]"
        placeholder={t('sign_page.comment_placeholder', 'Add a note to this page…') as string}
        value={text}
        maxLength={2000}
        onChange={(e) => setText(e.target.value)}
      />
      <div className="flex items-center justify-between mt-1.5">
        <span className="text-[10px] text-muted">
          {t('sign_page.comment_page', 'Page')} {currentPage}
        </span>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          disabled={!text.trim() || addMutation.isPending}
          onClick={() => addMutation.mutate()}
        >
          {addMutation.isPending ? t('common.saving', 'Saving…') : t('sign_page.comment_add', 'Add note')}
        </button>
      </div>
    </div>
  );
}

// A saved note anchored to a point on the page. Shows a small marker; the full
// text appears on hover. Read-only — placed notes cannot be edited once saved.
function NotePin({
  xPct,
  yPct,
  author,
  text,
}: {
  xPct: number;
  yPct: number;
  author: string;
  text: string;
}) {
  return (
    <div
      className="absolute group -translate-x-1/2 -translate-y-1/2 z-10"
      style={{ left: `${xPct}%`, top: `${yPct}%` }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="w-5 h-5 rounded-full bg-amber-400 border border-amber-600 text-[11px] flex items-center justify-center shadow cursor-default">
        🗒️
      </div>
      <div className="hidden group-hover:block absolute left-1/2 top-6 -translate-x-1/2 w-48 z-20 bg-amber-50 border border-amber-300 rounded-md shadow-lg px-2 py-1.5 text-left">
        <div className="text-[10px] font-semibold text-amber-800 mb-0.5">{author}</div>
        <div className="text-[11px] text-gray-900 whitespace-pre-wrap break-words">{text}</div>
      </div>
    </div>
  );
}

// Inline editor for a note being placed. Sits at the click point; clicks inside
// are stopped from bubbling so they don't drop a second pin.
function NoteDraftEditor({
  xPct,
  yPct,
  value,
  saving,
  onChange,
  onCancel,
  onSave,
}: {
  xPct: number;
  yPct: number;
  value: string;
  saving: boolean;
  onChange: (v: string) => void;
  onCancel: () => void;
  onSave: () => void;
}) {
  const { t } = useTranslation();
  // Keep the popover on-canvas: flip to the left/up when near the right/bottom.
  const flipX = xPct > 60;
  const flipY = yPct > 70;
  return (
    <div
      className="absolute z-30 w-52"
      style={{
        left: `${xPct}%`,
        top: `${yPct}%`,
        transform: `translate(${flipX ? '-100%' : '0'}, ${flipY ? '-100%' : '0'})`,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="bg-amber-50 border border-amber-400 rounded-md shadow-lg p-2">
        <textarea
          autoFocus
          className="input min-h-[64px] text-[12px] !bg-white"
          value={value}
          maxLength={2000}
          placeholder={t('sign_page.note_field_placeholder', 'Write your note…') as string}
          onChange={(e) => onChange(e.target.value)}
        />
        <div className="flex items-center justify-end gap-1.5 mt-1.5">
          <button type="button" className="btn btn-secondary btn-sm" onClick={onCancel}>
            {t('common.cancel', 'Cancel')}
          </button>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={!value.trim() || saving}
            onClick={onSave}
          >
            {saving ? t('common.saving', 'Saving…') : t('sign_page.comment_add', 'Add note')}
          </button>
        </div>
      </div>
    </div>
  );
}

function FieldOverlay({
  field,
  value,
  active,
  inert,
  muted,
  onClick,
}: {
  field: SignField;
  value: string;
  active: boolean;
  /** While note-placement mode is on — or during the review step — fields let
   *  clicks through so the click lands on the page and drops a note pin. */
  inert?: boolean;
  /** Review step: fields are shown faded, as a preview of where signing will
   *  happen, rather than as something to fill in right now. */
  muted?: boolean;
  onClick: () => void;
}) {
  const filled = value.trim().length > 0;
  const isNote = field.type === 'note';
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      className={cn(
        'absolute border-2 flex px-1 text-[11px] cursor-pointer overflow-hidden',
        inert && 'pointer-events-none',
        muted && 'opacity-40',
        isNote ? 'items-start justify-start' : 'items-center justify-center',
        // A note keeps its amber "sticky" look whether empty or filled so it
        // always reads as a note area rather than a form value.
        isNote
          ? 'border-amber-400 bg-amber-50 text-amber-900'
          : filled
            ? 'border-success bg-success-bg text-success'
            : active
              ? 'border-primary bg-primary-50 text-primary animate-pulse'
              : 'border-warning bg-warning-bg text-warning',
        isNote && active && 'ring-2 ring-primary',
      )}
      style={{
        left: `${field.posX}%`,
        top: `${field.posY}%`,
        width: `${field.width}%`,
        height: `${field.height}%`,
        borderRadius: 4,
      }}
    >
      {isNote ? (
        filled ? (
          <span className="w-full h-full overflow-hidden whitespace-pre-wrap text-left leading-tight py-0.5">
            {value}
          </span>
        ) : (
          <span className="font-semibold">🗒️ {prettyType(field.type)}{field.isRequired ? ' *' : ''}</span>
        )
      ) : filled ? (
        field.type === 'signature' || field.type === 'initial' || field.type === 'image' ? (
          <img src={value} alt="" className="max-h-full max-w-full" />
        ) : field.type === 'checkbox' ? (
          value === 'true' ? '✓' : ''
        ) : (
          <span className="truncate">{value}</span>
        )
      ) : (
        <span className="font-semibold">
          {field.isRequired ? `${prettyType(field.type)} *` : prettyType(field.type)}
        </span>
      )}
    </button>
  );
}

function FieldInputPanel({
  ctx,
  activeFieldId,
  values,
  onJump,
  onChange,
}: {
  ctx: SignContext;
  activeFieldId: string | null;
  values: Record<string, string>;
  onJump: (field: SignField) => void;
  onChange: (id: string, value: string) => void;
}) {
  const { t } = useTranslation();
  const active = ctx.fields.find((f) => f.id === activeFieldId);

  return (
    <div className="space-y-4 sticky top-20">
      {/* Quick list of all fields */}
      <div className="card !p-3">
        <div className="text-[11px] uppercase font-semibold text-muted tracking-wide mb-2">
          {t('sign_page.required_fields')}
        </div>
        <ul className="space-y-1 max-h-[200px] overflow-y-auto">
          {ctx.fields.map((f) => {
            const filled = (values[f.id] ?? '').trim().length > 0;
            return (
              <li key={f.id}>
                <button
                  onClick={() => onJump(f)}
                  className={cn(
                    'w-full text-left flex items-center gap-2 px-2 py-1.5 rounded text-[12px]',
                    activeFieldId === f.id
                      ? 'bg-primary-50 text-primary'
                      : 'text-gray-700 hover:bg-gray-100',
                  )}
                >
                  <span className={cn(
                    'w-3 h-3 rounded-full inline-block shrink-0',
                    filled ? 'bg-success' : f.isRequired ? 'bg-warning' : 'bg-gray-300',
                  )} />
                  <span>{t(`fields_step.type_${f.type}`)}</span>
                  <span className="text-muted ml-auto">{t('common.page')} {f.pageNumber}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Active field input */}
      <div className="card !p-3">
        <div className="text-[11px] uppercase font-semibold text-muted tracking-wide mb-2">
          {active ? t('sign_page.fill_field', { type: t(`fields_step.type_${active.type}`) }) : t('sign_page.click_field_on_page')}
        </div>
        {!active ? (
          <p className="text-[13px] text-muted">
            {t('sign_page.click_highlighted_field')}
          </p>
        ) : (
          <FieldInput
            field={active}
            value={values[active.id] ?? ''}
            onChange={(v) => onChange(active.id, v)}
            savedSignatureUrl={ctx.recipient.savedSignatureUrl}
            savedInitialUrl={ctx.recipient.savedInitialUrl}
            recipientName={ctx.recipient.name}
          />
        )}
      </div>
    </div>
  );
}

function FieldInput({
  field,
  value,
  onChange,
  savedSignatureUrl,
  savedInitialUrl,
  recipientName,
}: {
  field: SignField;
  value: string;
  onChange: (v: string) => void;
  savedSignatureUrl?: string | null;
  savedInitialUrl?: string | null;
  recipientName?: string;
}) {
  const { t } = useTranslation();
  if (field.type === 'signature' || field.type === 'initial') {
    const saved = field.type === 'signature' ? savedSignatureUrl : savedInitialUrl;
    return (
      <SignatureFieldChoice
        kind={field.type}
        saved={saved ?? null}
        value={value}
        onChange={onChange}
        recipientName={recipientName ?? ''}
      />
    );
  }
  if (field.type === 'checkbox') {
    return (
      <label className="flex items-center gap-2 text-[14px] cursor-pointer">
        <input
          type="checkbox"
          checked={value === 'true'}
          onChange={(e) => onChange(e.target.checked ? 'true' : 'false')}
          className="accent-primary w-5 h-5"
        />
        {t('sign_page.check_box')}
      </label>
    );
  }
  if (field.type === 'date') {
    return (
      <input
        type="text"
        className="input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  if (field.type === 'number') {
    return (
      <input
        type="number"
        className="input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  if (field.type === 'note') {
    return (
      <textarea
        className="input min-h-[80px] text-[13px]"
        value={value}
        maxLength={2000}
        onChange={(e) => onChange(e.target.value)}
        placeholder={t('sign_page.note_field_placeholder', 'Write your note…') as string}
      />
    );
  }
  return (
    <input
      type="text"
      className="input"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={prettyType(field.type)}
    />
  );
}

/**
 * Signature/initial input with two tabs:
 *   - "Use saved"  — auto-fills with the recipient's previously stored signature
 *                    (only shown if a saved image came back in the sign context)
 *   - "Draw new"   — fresh canvas, falls back to this when no saved exists
 */
function SignatureFieldChoice({
  kind,
  saved,
  value,
  onChange,
  recipientName,
}: {
  kind: 'signature' | 'initial';
  saved: string | null;
  value: string;
  onChange: (v: string) => void;
  recipientName: string;
}) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<'saved' | 'draw' | 'type'>(saved ? 'saved' : 'draw');

  // When the saved tab opens (or the saved url arrives), auto-fill the value
  // so the user can submit immediately without an extra click. The "type" mode
  // is driven by FontSignaturePad's own emit, so it isn't handled here.
  useEffect(() => {
    if (mode === 'saved' && saved && value !== saved) {
      onChange(saved);
    }
    if (mode === 'draw' && value === saved) {
      onChange('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, saved]);

  const tab = (m: 'saved' | 'draw' | 'type', label: string) => (
    <button
      type="button"
      onClick={() => setMode(m)}
      className={`px-3 py-1 rounded-md text-[12px] font-medium ${
        mode === m ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700'
      }`}
    >
      {label}
    </button>
  );

  // Initials field → first letters of each name part; signature → full name.
  const defaultText =
    kind === 'initial'
      ? recipientName
          .split(/\s+/)
          .map((s) => s[0] ?? '')
          .join('')
          .toUpperCase()
          .slice(0, 4)
      : recipientName;

  return (
    <div>
      <p className="text-[12px] text-muted mb-2">{t(`sign_page.draw_${kind}`)}</p>

      <div className="flex gap-1 mb-2 flex-wrap">
        {saved && tab('saved', `✓ ${t('sign_page.use_saved')}`)}
        {tab('draw', `✎ ${t('sign_page.draw_new')}`)}
        {tab('type', `⌨ ${t('sign_page.type_signature')}`)}
      </div>

      {mode === 'saved' && saved ? (
        <div className="border border-border rounded-md p-3 bg-gray-50 inline-block max-w-full">
          <img src={saved} alt="" className="max-h-24 max-w-full" />
        </div>
      ) : mode === 'type' ? (
        <FontSignaturePad defaultText={defaultText} placeholder={defaultText} onChange={onChange} />
      ) : (
        <SignaturePad onChange={onChange} />
      )}
    </div>
  );
}

function DeclineModal({ onClose, onSubmit }: { onClose: () => void; onSubmit: (reason: string) => void }) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  return (
    <div
      className="fixed inset-0 bg-gray-900/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div className="bg-white rounded-lg border border-border w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-border">
          <h3 className="text-base font-semibold text-gray-900">{t('sign_page.decline_modal_title')}</h3>
          <p className="text-[12px] text-muted mt-1">{t('sign_page.decline_modal_subtitle')}</p>
        </div>
        <div className="p-5">
          <label className="label">{t('sign_page.decline_reason')}</label>
          <textarea
            className="textarea min-h-[100px]"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('sign_page.decline_reason_placeholder')}
          />
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-border">
          <button onClick={onClose} className="btn btn-secondary">{t('common.cancel')}</button>
          <button
            onClick={() => onSubmit(reason)}
            className="btn btn-danger"
            disabled={reason.trim().length === 0}
          >
            {t('sign_page.submit_decline')}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Modal that lets the signer reject the envelope but specifically request a
 * revision — the backend opens a new R+1 draft and notifies the creator
 * with the reason text. Different visual treatment from plain decline:
 * warning-coloured (not danger) since the workflow continues vs. dies.
 */
function RevisionRequestModal({
  onClose,
  onSubmit,
  pending,
}: {
  onClose: () => void;
  onSubmit: (reason: string) => void;
  pending: boolean;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  return (
    <div
      className="fixed inset-0 bg-gray-900/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg border border-border w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-border">
          <h3 className="text-base font-semibold text-gray-900">
            {t('sign_page.revision_modal_title')}
          </h3>
          <p className="text-[12px] text-muted mt-1">
            {t('sign_page.revision_modal_subtitle')}
          </p>
        </div>
        <div className="p-5">
          <label className="label">{t('sign_page.revision_reason')}</label>
          <textarea
            className="textarea min-h-[100px]"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('sign_page.revision_reason_placeholder') as string}
            minLength={3}
          />
          <p className="text-[11px] text-muted mt-1">
            {t('sign_page.revision_reason_hint')}
          </p>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-border">
          <button onClick={onClose} className="btn btn-secondary" disabled={pending}>
            {t('common.cancel')}
          </button>
          <button
            onClick={() => onSubmit(reason)}
            className="btn btn-primary"
            disabled={pending || reason.trim().length < 3}
          >
            {pending ? t('common.submitting') : t('sign_page.submit_revision_request')}
          </button>
        </div>
      </div>
    </div>
  );
}

function SuccessCard({ ctx }: { ctx: SignContext }) {
  const { t } = useTranslation();
  const isForInfo = ctx.recipient.role === 'for_info';
  return (
    <div className="max-w-md mx-auto">
      <div className="card text-center">
        <div className="w-14 h-14 mx-auto rounded-full bg-success-bg text-success flex items-center justify-center text-3xl">
          ✓
        </div>
        <h2 className="text-[20px] font-semibold text-gray-900 mt-3">{t('sign_page.thank_you', { name: ctx.recipient.name })}</h2>
        <p className="text-[13px] text-muted mt-1">
          {isForInfo
            ? <>{t('sign_page.you_have_acknowledged')} <strong className="text-gray-900">{ctx.envelope.title}</strong>.</>
            : <>{t('sign_page.you_have_signed')} <strong className="text-gray-900">{ctx.envelope.title}</strong>.</>}
        </p>
        {!isForInfo && (
          <p className="text-[12px] text-muted mt-3">
            {t('sign_page.notify_text')}{' '}
            <strong>{ctx.recipient.email}</strong> {t('sign_page.notify_when_completed')}
          </p>
        )}
        <BackToHomeButton />
      </div>
    </div>
  );
}

function DeclinedCard() {
  const { t } = useTranslation();
  return (
    <div className="max-w-md mx-auto">
      <div className="card text-center">
        <div className="w-14 h-14 mx-auto rounded-full bg-danger-bg text-danger flex items-center justify-center text-3xl">
          ✕
        </div>
        <h2 className="text-[20px] font-semibold text-gray-900 mt-3">{t('sign_page.declined_title')}</h2>
        <p className="text-[13px] text-muted mt-2">
          {t('sign_page.declined_text')}
        </p>
        <BackToHomeButton />
      </div>
    </div>
  );
}

/**
 * Customer ask: after signing, the user should be able to navigate back to
 * the home page. We send them to /dashboard if they have an active session
 * (cookie/refresh token still valid) or to / (login) otherwise — the root
 * RootRedirect handles the branching.
 */
function BackToHomeButton() {
  const { t } = useTranslation();
  return (
    <div className="mt-5">
      <a href="/" className="btn btn-primary inline-block">
        {t('sign_page.back_to_home')}
      </a>
    </div>
  );
}

function ErrorCard({ message }: { message: string }) {
  const { t } = useTranslation();
  return (
    <div className="max-w-md mx-auto">
      <div className="card text-center">
        <div className="text-3xl mb-2">⚠️</div>
        <h2 className="text-[18px] font-semibold text-gray-900">{t('sign_page.error_title')}</h2>
        <p className="text-[13px] text-muted mt-2">{message}</p>
        <p className="text-[12px] text-muted mt-3">
          {t('sign_page.error_help')}
        </p>
      </div>
    </div>
  );
}

function prettyType(t: SignField['type']): string {
  return t.charAt(0).toUpperCase() + t.slice(1);
}

/**
 * Inline chain view rendered on the consent stage so the signer sees their
 * position in the sequence and who has signed before/will sign after them.
 * CC/for_info recipients are excluded — they don't gate the workflow.
 */
function SignChainView({ ctx }: { ctx: SignContext }) {
  const { t } = useTranslation();
  if (!ctx.chain || ctx.chain.length === 0) return null;
  const signers = ctx.chain
    .filter((r) => r.role !== 'cc' && r.role !== 'for_info')
    .sort((a, b) => a.signingOrder - b.signingOrder);
  if (signers.length <= 1) return null;

  type StepKind = 'done' | 'declined' | 'current' | 'waiting' | 'me';
  const stepKind = (idx: number, r: SignContext['chain'][number]): StepKind => {
    if (r.status === 'signed') return 'done';
    if (r.status === 'declined') return 'declined';
    if (ctx.envelope.workflowType === 'parallel') return r.isMe ? 'me' : 'waiting';
    const prevDone = signers.slice(0, idx).every((p) => p.status === 'signed');
    if (r.isMe && prevDone) return 'me';
    return prevDone ? 'current' : 'waiting';
  };

  return (
    <>
      <hr className="my-4 border-border" />
      <div className="text-[12px] uppercase font-semibold text-muted tracking-wide mb-3">
        {ctx.envelope.workflowType === 'sequential'
          ? t('sign_page.chain_title_seq')
          : t('sign_page.chain_title_par')}
      </div>
      <div className="flex items-stretch gap-0 overflow-x-auto">
        {signers.map((r, idx) => {
          const kind = stepKind(idx, r);
          const isLast = idx === signers.length - 1;
          return (
            <div key={r.id} className="flex items-center flex-1 min-w-[120px]">
              <div className="flex flex-col items-center text-center flex-1">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-[12px] font-semibold border-2 ${
                    kind === 'done'
                      ? 'bg-success text-white border-success'
                      : kind === 'declined'
                      ? 'bg-danger text-white border-danger'
                      : kind === 'me'
                      ? 'bg-primary text-white border-primary animate-pulse'
                      : kind === 'current'
                      ? 'bg-warning-bg text-warning-fg border-warning'
                      : 'bg-gray-50 text-muted border-gray-200'
                  }`}
                >
                  {kind === 'done' ? '✓' : kind === 'declined' ? '✕' : r.signingOrder}
                </div>
                <div
                  className={`mt-1.5 text-[11px] leading-tight line-clamp-2 ${
                    r.isMe ? 'font-semibold text-primary' : 'text-gray-800'
                  }`}
                >
                  {r.isMe ? t('sign_page.chain_you') : r.name}
                </div>
                {kind === 'me' && (
                  <div className="text-[9px] uppercase tracking-wide text-primary mt-0.5">
                    {t('sign_page.chain_your_turn')}
                  </div>
                )}
                {kind === 'current' && !r.isMe && (
                  <div className="text-[9px] uppercase tracking-wide text-warning-fg mt-0.5">
                    {t('sign_page.chain_pending')}
                  </div>
                )}
              </div>
              {!isLast && (
                <div
                  className={`h-0.5 w-4 ${kind === 'done' ? 'bg-success' : 'bg-gray-200'}`}
                />
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}
